/**
 * Atualização do Instrumento de Avaliação (desktop) — orquestrador.
 *
 * Mantém o estado e roteia por papel: presidência/admin entram na CONFERÊNCIA;
 * avaliadores na REVISÃO (executor pode alternar). Toda a tradução técnico→leigo
 * vive em `migracao/dominio.ts` (puro/testável); as telas em `migracao/telas.tsx`.
 *
 * Nenhuma invariante de segurança muda: as decisões/aprovação/execução/reversão
 * usam os endpoints existentes; a Conferência mescla o pré-flight REAL (READ-ONLY)
 * ao checklist amigável para nunca enfraquecer o gate. O texto não auto-publica.
 */
import type { PapelKey } from "@caint/shared";
import { useCallback, useEffect, useState } from "react";
import type {
  ImpactoRemoto,
  ItemMigracaoRemoto,
  LoteDetalheRemoto,
  LoteRemoto,
  PreflightRemoto,
  ResultadoExecucaoRemoto,
} from "../../lib/api.ts";
import type { MigracaoApi } from "../../state/useAppState.ts";
import { Modal } from "../ui/kit.tsx";
import { Ico } from "./migracao/componentes.tsx";
import {
  type AcaoAvaliador,
  acaoParaEfeito,
  conferenciaDeSeguranca,
  frasearResultado,
  mesclarPreflight,
  precisaConfirmacaoClinica,
} from "./migracao/dominio.ts";
import {
  Conferencia,
  ConfirmacaoClinica,
  ConfirmarAplicacao,
  ImpactoModal,
  Lista,
  Revisao,
} from "./migracao/telas.tsx";
import "./migracao/workbench.css";

const AVALIADORES: ReadonlySet<PapelKey> = new Set(["gqa", "ggo", "ncig", "audin", "coord"]);
const EXECUTORES: ReadonlySet<PapelKey> = new Set(["presidente", "admin"]);

type Vista = "revisao" | "conferencia";

function baixarJson(nome: string, dados: unknown) {
  const blob = new Blob([JSON.stringify(dados, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  a.click();
  URL.revokeObjectURL(url);
}

export interface MigracaoWorkbenchProps {
  api: MigracaoApi;
  papel: PapelKey;
  autor: string;
}

export function MigracaoWorkbench({ api, papel }: MigracaoWorkbenchProps) {
  const podeDecidir = AVALIADORES.has(papel) || EXECUTORES.has(papel);
  const ehExecutor = EXECUTORES.has(papel);
  const ehTI = papel === "admin";

  const [lotes, setLotes] = useState<LoteRemoto[] | null>(null);
  const [sel, setSel] = useState<LoteDetalheRemoto | null>(null);
  const [pf, setPf] = useState<PreflightRemoto | null>(null);
  const [vista, setVista] = useState<Vista>("conferencia");
  const [mostrarTecnico, setMostrarTecnico] = useState(false);
  const [impacto, setImpacto] = useState<{ item: ItemMigracaoRemoto; dados: ImpactoRemoto } | null>(null);
  const [clinica, setClinica] = useState<ItemMigracaoRemoto | null>(null);
  const [confirmar, setConfirmar] = useState(false);
  const [desfazerAberto, setDesfazerAberto] = useState(false);
  const [resultado, setResultado] = useState<ResultadoExecucaoRemoto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const erroDe = useCallback((e: unknown) => setErro(e instanceof Error ? e.message : String(e)), []);

  const carregarLotes = useCallback(() => {
    api.listar().then(setLotes).catch(erroDe);
  }, [api, erroDe]);
  useEffect(() => {
    carregarLotes();
  }, [carregarLotes]);

  // Carrega o detalhe do lote + o pré-flight real (READ-ONLY) para a Conferência.
  const carregarDetalhe = useCallback(
    async (id: string) => {
      const detalhe = await api.lote(id);
      setSel(detalhe);
      try {
        setPf(await api.preflight(id));
      } catch {
        setPf(null); // best-effort: a Conferência usa o checklist do front
      }
    },
    [api],
  );

  const abrirLote = useCallback(
    (id: string) => {
      setErro(null);
      setResultado(null);
      setVista(ehExecutor ? "conferencia" : "revisao");
      carregarDetalhe(id).catch(erroDe);
    },
    [carregarDetalhe, ehExecutor, erroDe],
  );

  const correr = useCallback(
    async (fn: () => Promise<void>) => {
      setErro(null);
      setOcupado(true);
      try {
        await fn();
      } catch (e) {
        erroDe(e);
      } finally {
        setOcupado(false);
      }
    },
    [erroDe],
  );

  const recarregar = useCallback(() => {
    if (sel) {
      return carregarDetalhe(sel.lote.id);
    }
    return Promise.resolve();
  }, [sel, carregarDetalhe]);

  const aplicarDecisao = (item: ItemMigracaoRemoto, acao: AcaoAvaliador) =>
    correr(async () => {
      if (!sel) return;
      const { decisao, codigoDestino } = acaoParaEfeito(item, acao);
      await api.decidir(sel.lote.id, item.codigoOrigem, decisao, codigoDestino);
      await recarregar();
    });

  // "Aceitar" em item sensível pede confirmação clínica antes de transferir.
  const decidir = (item: ItemMigracaoRemoto, acao: AcaoAvaliador) => {
    if (acao === "aceitar" && precisaConfirmacaoClinica(item)) {
      setClinica(item);
      return;
    }
    aplicarDecisao(item, acao);
  };

  const verImpacto = (item: ItemMigracaoRemoto) =>
    correr(async () => setImpacto({ item, dados: await api.impacto(item.codigoOrigem) }));

  // Aplicar = aprovar (se ainda não) + executar. Os endpoints reais validam tudo
  // de novo (AprovarLote exige todos decididos; ExecutarMigracao roda o pré-flight).
  const aplicar = () =>
    correr(async () => {
      if (!sel) return;
      if (sel.lote.estado !== "aprovado") {
        await api.aprovar(sel.lote.id);
      }
      const r = await api.executar(sel.lote.id);
      setResultado(r);
      setConfirmar(false);
      await recarregar();
      carregarLotes();
      // TODO(TI): sem canal de notificação automática. O aviso de "pronto para
      // publicar" é hoje o banner + a pílula só-admin + a auditoria do servidor.
      // Quando houver canal (e-mail/push ao admin), disparar aqui.
    });

  const desfazer = () =>
    correr(async () => {
      if (!sel) return;
      await api.reverter(sel.lote.id);
      setDesfazerAberto(false);
      await recarregar();
      carregarLotes();
    });

  const baixar = () =>
    correr(async () => {
      if (!sel) return;
      const m = await api.manifesto(sel.lote.id);
      if (m) baixarJson(`atualizacao-${sel.lote.id}-para-ti.json`, m);
    });

  const iniciar = (file: File) =>
    correr(async () => {
      const proposta = JSON.parse(await file.text());
      await api.ingerir(proposta);
      carregarLotes();
    });

  if (!sel) {
    return (
      <Lista
        lotes={lotes}
        erro={erro}
        ocupado={ocupado}
        ehExecutor={ehExecutor}
        onIniciar={iniciar}
        onAbrir={abrirLote}
      />
    );
  }

  const { lote, itens } = sel;
  const conf = pf ? mesclarPreflight(conferenciaDeSeguranca(itens), pf) : conferenciaDeSeguranca(itens);
  const aoFechar = () => setSel(null);
  const alternar = () => setVista((v) => (v === "conferencia" ? "revisao" : "conferencia"));
  const toggleTecnico = () => setMostrarTecnico((t) => !t);

  return (
    <>
      {vista === "conferencia" && ehExecutor ? (
        <Conferencia
          lote={lote}
          itens={itens}
          conf={conf}
          resultadoFrases={resultado ? frasearResultado(resultado.resumo) : null}
          mostrarTecnico={mostrarTecnico}
          ehTI={ehTI}
          ocupado={ocupado}
          onToggleTecnico={toggleTecnico}
          onVoltar={aoFechar}
          onAlternar={alternar}
          onImpacto={verImpacto}
          onAplicar={() => setConfirmar(true)}
          onDesfazer={() => setDesfazerAberto(true)}
          onBaixar={baixar}
          erro={erro}
        />
      ) : (
        <Revisao
          lote={lote}
          itens={itens}
          podeDecidir={podeDecidir}
          mostrarTecnico={mostrarTecnico}
          podeAlternar={ehExecutor}
          ehTI={ehTI}
          onToggleTecnico={toggleTecnico}
          onVoltar={aoFechar}
          onAlternar={alternar}
          onDecidir={decidir}
          onImpacto={verImpacto}
          erro={erro}
        />
      )}

      {impacto && <ImpactoModal item={impacto.item} dados={impacto.dados} onClose={() => setImpacto(null)} />}
      {clinica && (
        <ConfirmacaoClinica
          item={clinica}
          ocupado={ocupado}
          onClose={() => setClinica(null)}
          onSim={() => {
            const it = clinica;
            setClinica(null);
            aplicarDecisao(it, "aceitar");
          }}
          onNao={() => {
            const it = clinica;
            setClinica(null);
            aplicarDecisao(it, "descartar");
          }}
        />
      )}
      {confirmar && (
        <ConfirmarAplicacao
          itens={itens}
          ocupado={ocupado}
          onConfirmar={aplicar}
          onClose={() => setConfirmar(false)}
        />
      )}
      {desfazerAberto && (
        <Modal
          titulo="Desfazer esta atualização?"
          sub="Reversível e seguro"
          onClose={() => setDesfazerAberto(false)}
          foot={
            <>
              <button
                type="button"
                className="m-btn"
                disabled={ocupado}
                onClick={() => setDesfazerAberto(false)}
              >
                Cancelar
              </button>
              <button type="button" className="m-btn primary" disabled={ocupado} onClick={desfazer}>
                <Ico name="undo" size={14} /> Sim, desfazer
              </button>
            </>
          }
        >
          <p style={{ fontSize: "var(--text-sm)", lineHeight: 1.5 }}>
            Tudo volta ao estado anterior a partir da cópia de segurança — as avaliações e evidências retornam
            aos itens originais. Você pode aplicar a atualização de novo depois, se quiser.
          </p>
        </Modal>
      )}
    </>
  );
}
